import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

const PacksForFormularyComponent = ({ formulary, product, clickHandler }) => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setDataLoading(true);
        const params = new URLSearchParams({
          formulary_id: formulary.formulary_id
        });
        const res = await fetch(
          `${API_URL}/api/get_prescriptions_for_formulary?${params}`
        );
        const d = await res.json();
        if (isMounted) {
          setData(d);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setDataLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [formulary]);


  // =============================
  // 🎨 COLORS
  // =============================
  const COLORS = [
    "#00BFA5", "#536DFE", "#FF6D00", "#C51162", "#8E24AA",
    "#1E88E5", "#43A047", "#E53935", "#FDD835", "#6D4C41"
  ];

  let lineData = [];
    if (data) {
        const periodMap = new Map();

        data.forEach(trust => {
        const name = trust.hospital_trust;

        trust.outpatients.forEach(row => {
            if (!periodMap.has(row.period)) {
            periodMap.set(row.period, { period: row.period });
            }
            periodMap.get(row.period)[`${name}_out`] = row.packs;
        });

        trust.inpatients.forEach(row => {
            if (!periodMap.has(row.period)) {
            periodMap.set(row.period, { period: row.period });
            }
            periodMap.get(row.period)[`${name}_in`] = row.packs;
        });
        });

        const toDate = (label) => {
            const [mon, yy] = String(label).split(" ");
            return new Date(`${mon} 01 20${yy}`);
        };
        lineData = Array.from(periodMap.values()).sort((a, b) => {
            return toDate(a.period) - toDate(b.period);
        });

    }

    const CustomLegend = ({ payload }) => {
    // trust names are the first half
    const trusts = [...new Set(
        payload.map(item =>
        item.value.replace(" — Outpatients", "")
                    .replace(" — Inpatients", "")
        )
    )];

    return (
    <div style={{ fontSize: 12, padding: "6px 0" }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Outpatients</div>
        {trusts.map((trust, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
                width: 30,
                height: 2,
                background: COLORS[i % COLORS.length]
            }} />
            <span>{trust}</span>
            </div>
        ))}

        <div style={{ fontWeight: 600, margin: "10px 0 4px 0" }}>Inpatients</div>
        {trusts.map((trust, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
                width: 30,
                height: 2,
                borderTop: `2px dashed ${COLORS[i % COLORS.length]}`
            }} />
            <span>{trust}</span>
            </div>
        ))}
        </div>
    );
    };

  return (
    <div
    style={{
        background:"#fff",
        borderRadius:"8px",
        width:"90%",
        maxHeight:"95%",
        padding:"18px",
        overflowY:"auto",
        display:"flex",
        flexDirection:"column"
    }}
    >
        <div
            style={{
            display:"flex",
            justifyContent:"space-between",
            alignItems:"center",
            borderBottom:"1px solid #eee",
            paddingBottom:"12px",
            marginBottom:"12px"
            }}
        >
            <div style={{ fontSize:"18px", fontWeight:600 }}>
            {formulary.formulary_name}
            </div>

            <button
            onClick={clickHandler}
            style={{
                border:"none",
                background:"transparent",
                fontSize:"20px",
                fontWeight:"bold",
                cursor:"pointer",
            }}
            >
            ✕
            </button>
        </div>

        {data && (
            <div style={{ width:"100%", height:"400px" }}>
            <ResponsiveContainer>
                <LineChart data={lineData}>
                    <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize:12 }} />
                    <YAxis tick={{ fontSize:12 }} />
                    <Tooltip />
                    <Legend
                        content={<CustomLegend />}
                        wrapperStyle={{
                            maxHeight: "120px",
                            overflowY: "auto",
                            paddingRight: "8px"
                        }}
                    />
                    {data.map((trust, i) => {
                    const name = trust.hospital_trust;
                    const color = COLORS[i % COLORS.length];
                    return (
                        <>
                        <Line
                            key={`${name}_out`}
                            type="monotone"
                            dataKey={`${name}_out`}
                            name={`${name} — Outpatients`}
                            stroke={color}
                            strokeWidth={3}
                            dot={{ r:2 }}
                            activeDot={{ r:4 }}
                        />
                        <Line
                            key={`${name}_in`}
                            type="monotone"
                            dataKey={`${name}_in`}
                            name={`${name} — Inpatients`}
                            stroke={color}
                            strokeDasharray="4 4"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r:3 }}
                        />
                        </>
                    );
                    })}
                </LineChart>
            </ResponsiveContainer>
        </div>
        )}
      
        {dataLoading && (
            <div style={{ padding:"20px", textAlign:"center" }}>
            Loading…
            </div>
        )}

        {!dataLoading && (!data || data.length === 0) && (
            <div style={{ padding:"20px", textAlign:"center" }}>
            No data for this formulary
            </div>
        )}

    </div>
  );
};

export default PacksForFormularyComponent;
